export const myCommunication = {
    slug: 'my-communication',
    no: '02',
    type: 'rich',
    title: 'My Communications',
    subtitle: 'The Cluttered Clarity',
    category: 'UX Design',
    sourceUrl: 'https://krisakshi3.wixsite.com/portfolio/my-communications-the-cluttereed-clariy',
    cover: '/api/files/95dfc24b-81c2-4073-b135-4b63cb604bd8',
    client: 'Wipro · Office of the CIO',
    timeline: '27 Jan, 15 May 2020 (4 months)',
    responsibilities: [
        'Requirement gathering',
        'User research & interviews',
        'Information architecture',
        'UI/UX design (Employee · Requester · Admin)',
        'SUS usability testing',
        'Design hand-off',
    ],
    overview:
        'Wipro’s internal communication platform relied on bulk email and an outdated portal, creating noise, low trust, and slow approvals. I reimagined it as a role-aware Internal Communication Portal for Employees, Requesters and Admins, consolidating six governance goals into one self-serve surface that cut information overload and unlocked data-driven tracking across the org.',
    prototype: null,
    prototypes: [
        {
            label: 'Mobile app prototype',
            href: 'https://xd.adobe.com/view/3bb2eb83-8f69-4614-beaa-26083d53853e-6ee1/',
        },
        {
            label: 'Desktop portal prototype',
            href: 'https://xd.adobe.com/view/09ac3b3e-ed1a-481a-abf5-38246eb5dc6c-5cff/',
        },
    ],
    blocks: [
        // ── Project description (from screenshot) ──────────────────
        {
            kind: 'section',
            heading: 'About the project',
            subheading: 'The Internal Communication Portal',
            paragraph:
                'The internal communication platform was outdated and not user friendly. This project reimagines it end-to-end as a role-aware Internal Communication Portal, replacing bulk email as the default channel for daily employee broadcasts, and giving Employees, Requesters and Admins a single, self-serve surface to consume, create and govern communication. The redesigned portal streamlines the daily internal-communication process by consolidating six clear goals into one governed experience, from automating the broadcast approval flow, to minimising information overload, to enabling data-driven tracking across the platform.',
        },
        {
            kind: 'bullets',
            heading: 'Project goals and objective',
            items: [
                'Automation of process',
                'Minimize information overload',
                'Better targetting',
                'Internal broadcast process',
                'Ability to track and analyze data',
                'Access across platform',
            ],
        },
        {
            kind: 'section',
            heading: 'My role and responsibilities',
        },
        {
            kind: 'grid-cards',
            items: [
                {
                    title: 'Requirement gathering',
                    body: 'Conducted user interviews and stakeholders to understand pain points. Analyzed the current internal communication process to identify challenges.',
                },
                {
                    title: 'User experience design',
                    body: 'Developed user personas and user stories to map out user needs. Created wireframes and prototypes to visualize the platform.',
                },
                {
                    title: 'UI Design implementation',
                    body: 'Designed a high-fidelity interface based on the established UI guidelines. Developed a component library to ensure consistent design.',
                },
            ],
        },
        {
            kind: 'section',
            heading: 'Project modules',
        },
        {
            kind: 'grid-cards',
            items: [
                {
                    title: 'Employees',
                    body: 'Interested on broadcast, read company announcements, the need to filter and search communications.',
                },
                {
                    title: 'Requesters',
                    body: 'Create communication, track status, send to relevant employees or to the whole or groups in the portal.',
                },
                {
                    title: 'Admin',
                    body: 'Manage user role and permissions. Track status of all communications and manage content for the right audience.',
                },
            ],
        },
        // ── Design-Thinking walk-through ────────────────────────────
        {
            kind: 'tabs',
            tabs: [
                // ── Empathise ────────────────────────────────────────
                {
                    label: 'Empathise',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'empathise',
                            number: '01',
                            title: 'Empathise',
                            items: [
                                'Understanding the current process',
                                'Employee survey and interviews',
                                'User persona creation',
                                'Competitors analysis',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Current broadcast process: Offline',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/8d0fe2ce-ac68-4782-a4f5-bbb1d13ffb26',
                        },
                        {
                            kind: 'section',
                            heading: 'Employee survey & interviews',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/a8812356-66f7-49bf-b235-c8a52a68b93f',
                        },
                        {
                            kind: 'section',
                            heading: 'User persona',
                        },
                        {
                            kind: 'quotes',
                            items: [
                                'On an average day, I receive approximately forty emails, 30% of which are completely irrelevant for me. 50% are related to my work and remaining are the ones which even if I miss will cause nil harm to anyone.',
                            ],
                        },
                        {
                            kind: 'persona',
                            image: '/api/files/16eff9aa-87e3-4f0a-a294-7de76c10e4f8',
                            name: 'Hi, I am Pavan Shukla.',
                            intro:
                                "I use intranet while in office to conduct the majority of my preliminary research. I am concerned only about the emails that are related to my project or the announcements made by my department. I streamline the emails hitting my inbox using Outlook filters. Whenever I get little time, I give a high-level overview to the mails and delete them one by one if I don't find them useful by looking at the subject line.",
                            stats: [
                                { label: 'Age', value: '32' },
                                { label: 'Profession', value: 'Project Architect' },
                                { label: 'Location', value: 'Electronic City, Bangalore' },
                                { label: 'Company', value: 'Wipro' },
                                { label: 'Department', value: 'Industrial & Engineering Services' },
                                { label: 'Experience', value: '9 yrs total · 6 at Wipro' },
                                { label: 'Tech', value: 'MacBook + iPhone' },
                                { label: 'Internet use', value: '7–8 hours / day' },
                            ],
                            goals: [
                                'Stay up-to-date with company related information',
                                'Solve communication related queries by connecting with the right people without the burden of managing time',
                                'Have a simpler mode of managing information than emails',
                            ],
                            wishlist: [
                                'A single place to see only what is relevant to his role',
                                'Filters that actually work, beyond subject-line matching',
                                'Fewer inbox clashes with meeting-heavy days',
                            ],
                            frustrations: [
                                'Outlook filters do not work because of inconsistency in subject lines of similar-themed emails',
                                'Forced to ignore group announcements and org-level broadcasts because of tight deadlines',
                                'Clashes with deadlines at the time of office collaborations',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: "Competitors' analysis",
                        },
                        {
                            kind: 'image',
                            src: '/api/files/f1b7f01f-e620-4dc5-87f0-eb499cd4d526',
                        },
                    ],
                },
                // ── Define ───────────────────────────────────────────
                {
                    label: 'Define',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'define',
                            number: '02',
                            title: 'Define',
                            items: [
                                'User tasks and user stories',
                                'Approval workflow',
                                'Project scoping · in creation',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'User epics and user stories',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'Employees',
                                    body: 'Interested on broadcast, read company announcements, the need to filter and search communications.',
                                },
                                {
                                    title: 'Requesters',
                                    body: 'As a requester, I should be able to create a broadcast, select the target audience, track the status of my broadcast and edit the content if required.',
                                },
                                {
                                    title: 'Admin',
                                    body: 'As an admin, I should be able to manage categories and groups and track all the communication that happens on the the right audience.',
                                },
                            ],
                        },
                        {
                            kind: 'image',
                            src: '/api/files/19b920d6-f66a-4d5b-b99e-a5ebdcbbfd39',
                        },
                        {
                            kind: 'section',
                            heading: 'New online broadcast process',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/a93c54af-078d-4d98-bf5f-774e0bbc952f',
                        },
                        {
                            kind: 'section',
                            heading: 'Project scoping',
                            paragraph:
                                'The internal broadcast process. Detailed content for process of distribution and communication was decided to be digitalized. The scope was to ensure that only the relevant information is shared with the right audience.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/13787968-7779-4439-a25b-3f08cba537a2',
                        },
                    ],
                },
                // ── Ideate ───────────────────────────────────────────
                {
                    label: 'Ideate',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'ideate',
                            number: '03',
                            title: 'Ideate',
                            items: [
                                "How might we's",
                                "Wipro's UI guidelines",
                                'Wireframes',
                                'Progress analysis',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: "The how might we's",
                            paragraph:
                                'How-Might-We statements reframe every insight as a solvable design question, inviting many possible answers rather than a single fix.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/0af37f24-bf8f-4c52-8856-4e9e9ebcbc12',
                        },
                        {
                            kind: 'sub-tabs',
                            heading: "Wipro's UI guidelines",
                            tabs: [
                                {
                                    label: 'Typography',
                                    blocks: [
                                        {
                                            kind: 'image',
                                            src: '/api/files/a7ca1ed3-686d-4171-a997-4325d0989d9f',
                                        },
                                    ],
                                },
                                {
                                    label: 'Colours',
                                    blocks: [
                                        {
                                            kind: 'image',
                                            src: '/api/files/a314a800-b6f1-44b3-a171-adc938aa6f4b',
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Wireframes',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/83be9a30-3804-4d2f-aee5-84f69364ab33',
                        },
                        {
                            kind: 'section',
                            heading: 'Progress analysis',
                        },
                    ],
                },
                // ── Prototype ────────────────────────────────────────
                {
                    label: 'Prototype',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'prototype',
                            number: '04',
                            title: 'Prototype',
                            items: [
                                'Visual design explorations',
                                'Creating the style guide',
                                'Final user interface design',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Visual design explorations',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/7000c41a-cdfc-4c6c-9591-e8e5081ea556',
                        },
                        {
                            kind: 'section',
                            heading: 'Style guide & final visual designs',
                            paragraph:
                                'An extensive style guide was developed to ensure that the UI was consistent across all the platforms.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/81e2c1cc-67ff-428d-84aa-41c5111c1bf4',
                        },
                        {
                            kind: 'sub-tabs',
                            heading: 'Final designs by persona',
                            tabs: [
                                {
                                    label: 'Employee',
                                    blocks: [
                                        {
                                            kind: 'image',
                                            src: '/api/files/ccc752c6-de80-477d-84d9-05129781a28e',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/b9a2498d-f16b-4103-b108-900ee685ae32',
                                        },
                                    ],
                                },
                                {
                                    label: 'Requester',
                                    blocks: [
                                        {
                                            kind: 'image',
                                            src: '/api/files/27ebebca-9e79-48df-bb8b-1ed96b4ab3ac',
                                        },
                                    ],
                                },
                                {
                                    label: 'Admin',
                                    blocks: [
                                        {
                                            kind: 'image',
                                            src: '/api/files/45dc3935-271a-4c83-995a-22db7d1414fd',
                                        },
                                    ],
                                },
                                {
                                    label: 'Help & preferences',
                                    blocks: [
                                        {
                                            kind: 'image',
                                            src: '/api/files/e2f6dc58-adc4-46c8-a9ac-c668c1b3023e',
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                // ── Deliver ──────────────────────────────────────────
                {
                    label: 'Deliver',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'deliver',
                            number: '05',
                            title: 'Deliver',
                            items: [
                                'SUS usability testing',
                                'The learning outcomes & conclusion',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'SUS usability testing',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/7a205149-dd70-48a6-8aea-3dd72905fb81',
                        },
                        {
                            kind: 'section',
                            heading: 'The learning outcomes & conclusion',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'The key design focus',
                                    body: 'Empathize with the user to understand the challenges.',
                                },
                                {
                                    title: 'Define steps',
                                    body: 'Creating user stories and wireframes.',
                                },
                                {
                                    title: 'The design workshop',
                                    body: 'The design thinking flow.',
                                },
                                {
                                    title: 'The online courses',
                                    body: 'Learning basic user experience, UI design and research.',
                                },
                                {
                                    title: 'Presentation',
                                    body: 'UI design + implementation on local server.',
                                },
                                {
                                    title: 'Implementation',
                                    body: 'Developed the portal with the help of development team.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
    next: { slug: 'cloudhub-enterprise-redesign', title: 'CloudHub Enterprise Redesign' },
};
